// src/components/Layout/Shell.js
import React, { useState, useEffect } from "react";
import { Outlet, useNavigate, Link } from "react-router-dom";
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  CssBaseline,
  Box,
  Divider,
  Avatar,
  useTheme,
  Dialog,
  DialogTitle,
  DialogActions,
  Button,
  CircularProgress,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import DashboardIcon from "@mui/icons-material/Dashboard";
import DescriptionIcon from "@mui/icons-material/Description";
import LogoutIcon from "@mui/icons-material/Logout";
import { logoutUser } from "../../api/authApi";
import { useSchool } from "../../context/SchoolContext"; // ✅ school context

const drawerWidth = 240;

export default function Shell() {
  const [open, setOpen] = useState(true);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const theme = useTheme();
  const navigate = useNavigate();

  // ✅ School context
  const { schoolData, loading } = useSchool();
  const [currentSchool, setCurrentSchool] = useState({});

  useEffect(() => {
    if (schoolData?.data?.length > 0) {
      setCurrentSchool(schoolData.data[0]);
    }
  }, [schoolData]);

  const toggleDrawer = () => {
    setOpen(!open);
  };

  const handleLogout = async () => {
    try {
      const token = localStorage.getItem("accessToken");
      if (token) {
        await logoutUser(token);
      }
      localStorage.removeItem("accessToken");
      localStorage.removeItem("refreshToken");
      navigate("/");
    } catch (error) {
      console.error("Logout failed:", error);
      localStorage.removeItem("accessToken");
      localStorage.removeItem("refreshToken");
      navigate("/");
    }
  };

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />

      {/* Top Navbar */}
      <AppBar
        position="fixed"
        color="primary"
        sx={{
          width: open ? `calc(100% - ${drawerWidth}px)` : "100%",
          ml: open ? `${drawerWidth}px` : 0,
          transition: "all 0.3s ease",
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="menu"
            onClick={toggleDrawer}
            edge="start"
            sx={{ mr: 2 }}
          >
            <MenuIcon />
          </IconButton>
          {loading ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            <Typography variant="h6" noWrap>
              {currentSchool.school_name || "School Dashboard"}
            </Typography>
          )}
        </Toolbar>
      </AppBar>

      {/* Sidebar Drawer */}
      <Drawer
        variant="persistent"
        anchor="left"
        open={open}
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: drawerWidth,
            boxSizing: "border-box",
            transition: "all 0.3s ease",
            bgcolor: "primary.main",
            color: "primary.contrastText",
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        {/* Sidebar Header with School Logo and Name */}
        <Box sx={{ p: 2, display: "flex", alignItems: "center", gap: 1 }}>
          {loading ? (
            <CircularProgress size={32} color="inherit" />
          ) : currentSchool.logo ? (
            <Avatar
              src={currentSchool.logo}
              sx={{
                width: 40,
                height: 40,
                bgcolor: theme.palette.background.default,
              }}
            />
          ) : (
            <Avatar
              sx={{
                bgcolor: theme.palette.background.default,
                color: theme.palette.primary.main,
                width: 40,
                height: 40,
              }}
            >
              {currentSchool.school_name
                ? currentSchool.school_name.charAt(0)
                : "S"}
            </Avatar>
          )}
          <Box sx={{ overflow: "hidden" }}>
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 500, lineHeight: 1.2 }}
            >
              {currentSchool.school_name || "School Name"}
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ borderColor: "rgba(255,255,255,0.3)" }} />

        {/* Sidebar Menu */}
        <List sx={{ p: 0 }}>
          <ListItem disablePadding>
            <ListItemButton
              component={Link}
              to="/dashboard"
              sx={{ color: "inherit", py: 1 }}
            >
              <DashboardIcon sx={{ mr: 1 }} />
              <ListItemText primary="Dashboard" />
            </ListItemButton>
          </ListItem>

          <ListItem disablePadding>
            <ListItemButton
              component={Link}
              to="/create-question"
              sx={{ color: "inherit", py: 1 }}
            >
              <DashboardIcon sx={{ mr: 1 }} />
              <ListItemText primary="Create Question" />
            </ListItemButton>
          </ListItem>

          <ListItem disablePadding>
            <ListItemButton
              component={Link}
              to="/pdf-view"
              sx={{ color: "inherit", py: 1 }}
            >
              <DescriptionIcon sx={{ mr: 1 }} />
              <ListItemText primary="My Papers" />
            </ListItemButton>
          </ListItem>

          <ListItem disablePadding>
            <ListItemButton
              component={Link}
              to="/school-profile"
              sx={{ color: "inherit", py: 1 }}
            >
              <DescriptionIcon sx={{ mr: 1 }} />
              <ListItemText primary="School Settings" />
            </ListItemButton>
          </ListItem>
        </List>

        {/* Sidebar Footer */}
        <Box
          sx={{
            mt: "auto",
            p: 1,
            borderTop: "1px solid rgba(255,255,255,0.3)",
          }}
        >
          <ListItemButton
            sx={{ color: "inherit", py: 0.5 }}
            onClick={() => setConfirmOpen(true)}
          >
            <LogoutIcon sx={{ mr: 1 }} />
            <ListItemText primary="Logout" />
          </ListItemButton>
          <Typography
            variant="caption"
            sx={{ mt: 0.5, display: "block", textAlign: "center" }}
          >
            © 2025 Dambaruu. Copyright.
          </Typography>
        </Box>
      </Drawer>

      {/* Main Content Area */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: open ? `calc(100% - ${drawerWidth}px)` : "100%",
          transition: "all 0.3s ease",
        }}
      >
        <Toolbar />
        <Outlet />
      </Box>

      {/* Logout Confirmation Dialog */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Are you sure you want to logout?</DialogTitle>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} color="secondary">
            No
          </Button>
          <Button
            onClick={() => {
              setConfirmOpen(false);
              handleLogout();
            }}
            color="primary"
            variant="contained"
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
